import { useMemo } from 'react';
import type { TFunction } from 'i18next';
import type {
  CompanyBasicResponse,
  CompanyFinancialsResponse,
  CompanyGesellschafterResponse,
  CompanyHistory,
  CompanyRegisterResponse,
} from '@/lib/backend-api';
import type { BalanceSheetFlatRow } from '@/new_components/balance_sheet/balanceSheetService';
import type { GuvFlatRow } from '@/new_components/guv/guvHistoryService';
import {
  buildCompanyProfileHeroData,
  buildCompanyProfileStatistics,
  getEffectiveCompanyRegisterData,
  getEffectiveCompanyFinancialsData,
} from './companyDetailUtils';
import type { createFormatters } from './currencyFormatters';
import {
  buildAiGuvRows,
  buildAktivaRows,
  buildGuvRows,
  buildPassivaRows,
  buildRevenueHistory,
  buildValidationErrors,
  collectAvailableFinancialYears,
  computeHasCostBreakdown,
  hasDownloadableFinancialDocs,
} from './companyDetailDerived';

// CompanyDetailPage's derived data, in two hooks it calls back to back (the
// memo order is unchanged). Inputs are the page's fetched state.

export function useCompanyDetailEffectiveData({
  basicData,
  registerData,
  gesellschafterData,
  financialsData,
  companyHistory,
  euId,
}: {
  basicData: CompanyBasicResponse | null;
  registerData: CompanyRegisterResponse | null;
  gesellschafterData: CompanyGesellschafterResponse | null;
  financialsData: CompanyFinancialsResponse | null;
  companyHistory: CompanyHistory | null;
  euId: string;
}) {
  // ==========================================================================
  // EFFECTIVE DATA - Derive from basicData for guests, use fetched data for
  // authenticated users. This lets guest SSR render all visible sections.
  // ==========================================================================

  // WARNING: basicData.overrides comes from verified company owners and MUST
  // take highest priority over register, AI, and backfill data wherever they
  // overlap (address fields, about_us). Never let another source override these.
  const effectiveRegisterData = useMemo(
    () => getEffectiveCompanyRegisterData(basicData, registerData),
    [registerData, basicData],
  );

  // /gesellschafter answers after its page-load DK visit, so its catalog is
  // fresher when non-empty. Keep documents and completeness from that same source.
  const registerDocumentsSource = gesellschafterData?.register_documents?.length
    ? gesellschafterData
    : effectiveRegisterData;

  const effectiveFinancialsData = useMemo(
    () => getEffectiveCompanyFinancialsData(basicData, financialsData, euId),
    [financialsData, basicData, euId],
  );

  // ==========================================================================
  // COMPUTED DATA - Memoized to prevent unnecessary re-renders
  // ==========================================================================

  const hasGesellschafterliste =
    basicData?.has_gesellschafterliste ||
    gesellschafterData?.has_gesellschafterliste ||
    false;
  const gesellschafterItems = gesellschafterData?.gesellschafter ?? [];

  const heroData = useMemo(
    () => buildCompanyProfileHeroData(
      basicData, effectiveRegisterData, companyHistory, euId,
    ),
    [basicData, effectiveRegisterData, companyHistory, euId],
  );

  return {
    effectiveRegisterData,
    registerDocumentsSource,
    effectiveFinancialsData,
    hasGesellschafterliste,
    gesellschafterItems,
    heroData,
  };
}

export type CompanyDetailEffectiveData = ReturnType<
  typeof useCompanyDetailEffectiveData
>;

export function useCompanyDetailFinancialViews({
  effectiveFinancialsData,
  t,
  fmt,
}: {
  effectiveFinancialsData: CompanyFinancialsResponse | null;
  t: TFunction;
  fmt: ReturnType<typeof createFormatters>;
}) {
  const statistics = useMemo(
    () => buildCompanyProfileStatistics(effectiveFinancialsData, t, fmt),
    [effectiveFinancialsData, t, fmt],
  );

  // Convert financial tables for existing components
  const aktivaData = useMemo<BalanceSheetFlatRow[] | null>(() => {
    return buildAktivaRows(effectiveFinancialsData);
  }, [effectiveFinancialsData]);

  const passivaData = useMemo<BalanceSheetFlatRow[] | null>(() => {
    return buildPassivaRows(effectiveFinancialsData);
  }, [effectiveFinancialsData]);

  const guvData = useMemo<GuvFlatRow[] | null>(() => {
    return buildGuvRows(effectiveFinancialsData);
  }, [effectiveFinancialsData]);

  const aiGuvData = useMemo<GuvFlatRow[] | null>(() => {
    return buildAiGuvRows(effectiveFinancialsData);
  }, [effectiveFinancialsData]);

  const mergedGuvData = useMemo(() => {
    return [...(guvData || []), ...(aiGuvData || [])];
  }, [guvData, aiGuvData]);

  // Mirror CompanyCostsPieChart's data condition so the chart/table toggle is
  // only rendered when at least one year has recognizable cost categories —
  // otherwise both the pie and the table return null and we'd show an empty
  // toggle bar.
  const hasCostBreakdown = useMemo(() => {
    return computeHasCostBreakdown(mergedGuvData, t);
  }, [mergedGuvData, t]);

  // Unique years present in financial data (for admin correction form)
  const availableFinancialYears = useMemo(() => {
    return collectAvailableFinancialYears(effectiveFinancialsData);
  }, [effectiveFinancialsData]);

  // Revenue history for trend chart
  const revenueHistory = useMemo(() => {
    return buildRevenueHistory(effectiveFinancialsData);
  }, [effectiveFinancialsData]);

  // Mirrors FinancialDocsCard's own filter — the mobile action bar must not
  // offer a "Bilanz" jump to a card that hides itself for lack of downloadable
  // file types.
  const hasFinancialDocs = useMemo(
    () => hasDownloadableFinancialDocs(effectiveFinancialsData),
    [effectiveFinancialsData],
  );

  // Validation errors for admin display
  const validationErrors = useMemo(() => {
    return buildValidationErrors(effectiveFinancialsData);
  }, [effectiveFinancialsData]);

  return {
    statistics,
    aktivaData,
    passivaData,
    guvData,
    aiGuvData,
    mergedGuvData,
    hasCostBreakdown,
    availableFinancialYears,
    revenueHistory,
    hasFinancialDocs,
    validationErrors,
  };
}

export type CompanyDetailFinancialViews = ReturnType<
  typeof useCompanyDetailFinancialViews
>;
